"use client";

import { ErrorCardContent } from "@/components/ui/shared/ErrorCardContent";
import FetchModal from "@/fetch-ui/FetchModal";
import { MIN_ROOMS_PER_NIGHT } from "../_logic/constants";

interface BelowMinRoomsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Why the modal is showing — changes the copy and, for `"count"`, offers a way forward.
   *  `"count"`: the planner entered fewer than {@link MIN_ROOMS_PER_NIGHT} rooms and can either
   *  fix the count or opt out of guestrooms entirely. `"northstar"`: a Northstar request answered
   *  "no guestrooms needed", which isn't a valid answer for this source — Northstar events are
   *  always sourced with guestrooms, so there's no bypass, just corrected copy explaining why. */
  reason: "count" | "northstar";
  /** Only used for `reason: "count"` — lets the planner proceed without guestrooms instead of
   *  being stuck re-triggering this modal every time they hit Continue below the minimum. */
  onContinueWithoutGuestrooms?: () => void;
}

const TITLES = {
  count: "We're not the right fit for your event",
  northstar: "Guestrooms are required for this request",
} as const;

/**
 * Popup shown when a guestrooms answer can't proceed as-is in the create event wizard — either
 * the entered count is below the platform minimum, or a Northstar request declined guestrooms
 * entirely (not a valid answer for that source). Reuses {@link ErrorCardContent} so it is
 * visually identical to the app's error card, inside a dismissable modal.
 * @param props.isOpen - Whether the modal is visible.
 * @param props.onClose - Dismiss handler; keeps the user on the guestrooms step.
 * @param props.reason - Which case triggered the modal (see {@link BelowMinRoomsModalProps.reason}).
 * @param props.onContinueWithoutGuestrooms - Forward action for the `"count"` case.
 * @returns The guestrooms-blocked explanation modal.
 */
export default function BelowMinRoomsModal({
  isOpen,
  onClose,
  reason,
  onContinueWithoutGuestrooms,
}: BelowMinRoomsModalProps) {
  // Derived values
  const title = TITLES[reason];
  const actions =
    reason === "count"
      ? [
          { label: "Edit count", onClick: onClose, variant: "outline" as const },
          ...(onContinueWithoutGuestrooms
            ? [
                {
                  label: "Continue without guestrooms",
                  onClick: onContinueWithoutGuestrooms,
                  variant: "primary" as const,
                },
              ]
            : []),
        ]
      : [{ label: "Got it", onClick: onClose, variant: "primary" as const }];

  // Render
  return (
    <FetchModal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={title}
      size="auto"
      className="w-full max-w-2xl bg-transparent p-0"
      hideClose
    >
      <ErrorCardContent
        title={title}
        message={
          reason === "count" ? (
            <>
              We specialize in multi-day events requiring{" "}
              <span className="font-semibold">{MIN_ROOMS_PER_NIGHT} or more hotel rooms</span> through our platform. As
              a certified travel agency, we leverage our accreditation to secure the best possible rates and terms for
              your overnight stays. You can add more rooms, or continue without booking guestrooms through us.
            </>
          ) : (
            <>
              Northstar requests are sourced through our platform with guestrooms included, so we can't proceed without
              them for this event. Go back and enter the guestrooms you need to continue.
            </>
          )
        }
        actions={actions}
        helpText="" // suppress the support footer — not relevant to this gate
      />
    </FetchModal>
  );
}
